import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getProgramBySlug } from '../../data/programs';
import { getProgress, setUnitCompleted, isProgramCompleted } from '../../lib/storage/progressStore';
import { getTests } from '../../lib/storage/testStore';
import { track } from '../../lib/analytics/track';
import AudioUnitPlayer from '../../components/player/AudioUnitPlayer';
import Navbar from '../../components/Navbar';
import SEOHead from '../../components/SEOHead';
import { ArrowLeft, Lock, ArrowRight, Star, Lightbulb, FlaskConical, Headphones, BookOpen } from 'lucide-react';
import apiClient from '../../services/apiClient';

export default function UnitPage() {
  const { programSlug, unitSlug } = useParams();
  const navigate = useNavigate();
  const program = getProgramBySlug(programSlug);
  const unit = program?.units.find((u) => u.slug === unitSlug);

  const [audioCompleted, setAudioCompleted] = useState(false);
  const [progress, setProgress] = useState({});
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackTopics, setFeedbackTopics] = useState([]);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [testCompleted, setTestCompleted] = useState(false);
  const unlockedContentRef = React.useRef(null);

  const availableTopics = [
    'Liderazgo',
    'Ventas',
    'Negociación',
    'Comunicación',
    'Productividad',
    'Estrategia',
    'Innovación',
    'Gestión de equipos'
  ];

  useEffect(() => {
    if (program) {
      const p = getProgress(program.slug);
      setProgress(p);
      
      // Si ya completó el audio antes, desbloquear test
      if (p[unitSlug]?.completedAt) {
        setAudioCompleted(true);
      }

      track('unit_view', { programSlug: program.slug, unitSlug });

      // Verificar si ya dio feedback
      const storedFeedback = localStorage.getItem(`feedback_${program.slug}_${unitSlug}`);
      if (storedFeedback) {
        setFeedbackSubmitted(true);
      }

      // Verificar si ya completó el test
      const tests = getTests(program.slug);
      if (tests[unitSlug]) {
        setTestCompleted(true);
      }
    }
  }, [program, unitSlug, programSlug]);

  if (!program || !unit) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Conversación no encontrada
            </h1>
            <Link
              to="/programas"
              className="text-blue-600 hover:text-blue-700 font-semibold"
            >
              ← Volver a programas
            </Link>
          </div>
        </div>
      </>
    );
  }

  const handleAudioComplete = () => {
    setAudioCompleted(true);
    const newProgress = setUnitCompleted(program.slug, unit.slug);
    setProgress(newProgress);
    track('unit_complete', { programSlug: program.slug, unitSlug });
    
    // Verificar si completó TODO el programa
    if (isProgramCompleted(program.slug, program.units.length)) {
      track('program_completed', { 
        programSlug: program.slug, 
        totalUnits: program.units.length,
        programTitle: program.title 
      });
    }

    // Scroll automático al contenido desbloqueado tras breve pausa
    setTimeout(() => {
      unlockedContentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 400);
  };

  const handleTopicToggle = (topic) => {
    setFeedbackTopics(prev => 
      prev.includes(topic) 
        ? prev.filter(t => t !== topic)
        : [...prev, topic]
    );
  };

  const handleFeedbackSubmit = async () => {
    if (feedbackRating === 0) {
      alert('Por favor selecciona una calificación (1-5 estrellas)');
      return;
    }

    try {
      await apiClient.post('/course-feedback', {
        programSlug: program.slug,
        unitSlug: unit.slug,
        rating: feedbackRating,
        topics: feedbackTopics,
        comment: feedbackComment.trim()
      });

      // Guardar localmente que ya dio feedback
      localStorage.setItem(`feedback_${program.slug}_${unitSlug}`, JSON.stringify({
        rating: feedbackRating,
        topics: feedbackTopics,
        submittedAt: new Date().toISOString()
      }));

      setFeedbackSubmitted(true);
      track('feedback_submit', { programSlug: program.slug, unitSlug, rating: feedbackRating });
    } catch (error) {
      console.error('Error al enviar feedback:', error);
      alert('Error al enviar feedback. Intenta de nuevo.');
    }
  };

  const isTestUnlocked = audioCompleted || progress[unit.slug]?.completedAt;
  const showFeedback = isTestUnlocked; // Mostrar después de completar audio

  // Encontrar siguiente unidad
  const currentIndex = program.units.findIndex((u) => u.slug === unit.slug);
  const nextUnit = program.units[currentIndex + 1];
  const isNextUnitLocked = nextUnit?.locked === true;

  // Schema.org con breadcrumbs para unidad
  const unitSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "name": "Breadcrumbs",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Inicio",
            "item": "https://edutechpulselab.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Micro-Programas",
            "item": "https://edutechpulselab.com/programas"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": program.title,
            "item": `https://edutechpulselab.com/p/${program.slug}`
          },
          {
            "@type": "ListItem",
            "position": 4,
            "name": unit.title,
            "item": `https://edutechpulselab.com/p/${program.slug}/u/${unit.slug}`
          }
        ]
      },
      {
        "@type": "LearningResource",
        "name": unit.title,
        "description": unit.objective,
        "learningResourceType": "Audio Lesson",
        "timeRequired": `PT${unit.durationMin}M`,
        "inLanguage": "es-ES",
        "isPartOf": {
          "@type": "Course",
          "name": program.title,
          "url": `https://edutechpulselab.com/p/${program.slug}`
        }
      }
    ]
  };

  return (
    <>
      <SEOHead
        title={`${unit.title} - ${program.title}`}
        description={`${unit.objective} Conversación de ${unit.durationMin} minutos basada en ${unit.source?.book || 'metodologías aplicadas'}.`}
        canonicalUrl={`/p/${program.slug}/u/${unit.slug}`}
        ogType="article"
        schema={unitSchema}
        robots={unit.locked ? 'noindex, follow' : 'index, follow, max-image-preview:large'}
      />
      <Navbar />
      <main className="min-h-screen" style={{ backgroundColor: '#F7F9FB' }}>
        {/* Header portada editorial */}
        <section className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 py-6 sm:py-8">
            <Link
              to={`/p/${program.slug}`}
              className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 mb-6 transition-colors text-sm"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Volver al programa
            </Link>

            <div className="space-y-2">
              <div className="text-xs font-bold text-slate-500 tracking-wider uppercase">
                CONVERSACIÓN {unit.order}
              </div>
              
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">
                {unit.title}
              </h1>
              
              {unit.source && (
                <p className="text-base text-slate-600">
                  Conversación sobre <span className="font-semibold">{unit.source.book}</span> ({unit.source.author})
                </p>
              )}
            </div>
          </div>
        </section>

        {/* ===== AUDIO PLAYER - STAGE ===== */}
        <section className="max-w-7xl mx-auto px-6 sm:px-8 py-8 sm:py-12">
          <div
            className="rounded-2xl overflow-hidden shadow-xl"
            style={{ background: 'linear-gradient(135deg, #0c1a2e 0%, #162944 60%, #0e2240 100%)' }}
          >
            {/* Franja de acento superior */}
            <div className="h-1 w-full bg-gradient-to-r from-cyan-400 to-blue-500" />

            <div className="px-4 sm:px-12 py-6 sm:py-10">
              {/* Metadatos del audio */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 text-cyan-300 text-xs font-semibold px-3 py-1.5 rounded-full">
                  <Headphones className="w-3.5 h-3.5" />
                  {unit.durationMin || unit.duration} min · Conversación {unit.order}
                </div>
                {unit.source && (
                  <div className="inline-flex items-center gap-2 text-white/40 text-xs">
                    <BookOpen className="w-3.5 h-3.5" />
                    <em>{unit.source.book}</em> · {unit.source.author}
                  </div>
                )}
              </div>

              {/* Frase introductoria */}
              {unit.preAudioPhrase ? (
                <p className="text-white/70 text-sm sm:text-base leading-relaxed mb-6 italic max-w-2xl">
                  &ldquo;{unit.preAudioPhrase}&rdquo;
                </p>
              ) : (
                <p className="text-white/60 text-sm leading-relaxed mb-6 max-w-2xl">
                  Mientras escuchas, pregúntate: ¿cómo estoy decidiendo yo en situaciones similares?
                </p>
              )}

              {/* Audio player — contraste claro sobre fondo oscuro */}
              <AudioUnitPlayer
                programSlug={program.slug}
                unitSlug={unit.slug}
                src={unit.audioUrl}
                onComplete={handleAudioComplete}
              />

              {/* Aviso post-audio / CTA cuando ya completó */}
              {isTestUnlocked ? (
                <button
                  onClick={() => unlockedContentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/30 rounded-lg text-emerald-300 text-sm font-semibold transition-colors"
                >
                  <ArrowRight className="w-4 h-4" />
                  Ver ideas clave y responder Reflexión guiada
                </button>
              ) : (
                <p className="mt-4 text-white/40 text-xs text-center leading-relaxed">
                  Escucha completo para marcar como completado<br />
                  y desbloquear ideas clave + reflexión guiada.
                </p>
              )}
            </div>
          </div>
        </section>

        {/* ===== CONTENIDO EDITORIAL - DESPUÉS DEL AUDIO ===== */}
        <section className="max-w-7xl mx-auto px-6 sm:px-8 py-8 sm:py-12">
          <div className="bg-slate-50 rounded-2xl border border-slate-200 px-6 sm:px-8 py-8 sm:py-10 space-y-8">
            
            {/* Contexto de esta conversación */}
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3">
                Contexto de esta conversación
              </h2>
              <p className="text-base text-slate-700 leading-relaxed mb-3">
                {unit.objective}
              </p>
              <p className="text-base text-slate-700 leading-relaxed">
                Esta unidad explora las ideas centrales de <strong>{unit.source ? unit.source.book : 'metodologías aplicadas'}</strong>
                {unit.source && ` de ${unit.source.author}`}, no como teoría académica sino como marco aplicable en contextos reales de decisión y actuación profesional. 
                No se trata de memorizar conceptos, sino de contrastar tu criterio actual con modelos mentales más robustos.
              </p>
            </div>

            {/* Por qué importa */}
            <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-cyan-600" />
                Por qué importa
              </h3>
              <p className="text-base text-slate-700 leading-relaxed">
                {unit.whyMatters
                  ? unit.whyMatters
                  : (
                    (unit.slug.includes('psicologia') && 'Porque las decisiones estratégicas rara vez son puramente racionales. Entender los disparadores psicológicos que influyen en tus clientes, colegas o equipo te permite diseñar conversaciones que reduzcan fricción cognitiva y faciliten la acción sin manipulación.')
                    || (unit.slug.includes('spin') && 'Porque diagnosticar bien es más valioso que presentar bien. Las ventas complejas se ganan en la fase de descubrimiento, no en el pitch. Dominar SPIN Selling te permite convertir síntomas vagos en urgencia real y hacer que el cliente articule el valor de tu solución antes de que tú lo presentes.')
                    || (unit.slug.includes('challenger') && 'Porque desafiar la narrativa del cliente no es cuestionar su inteligencia, sino ayudarlo a ver ángulos ciegos. Los mejores vendedores no solo responden necesidades expresadas; redefinen cómo el cliente entiende su problema. Esto requiere autoridad técnica, no carisma.')
                    || (unit.slug.includes('reconocer') && 'Porque nuestros sesgos cognitivos distorsionan información crítica justo cuando más necesitamos claridad. Reconocer sesgos de confirmación, disponibilidad o anclaje en tiempo real te permite tomar decisiones más confiables, especialmente en contextos de alta presión o incertidumbre.')
                    || (unit.slug.includes('modelos') && 'Porque los modelos mentales que usas para entender el mundo determinan las decisiones que tomas. Modelos mentales pobres producen decisiones pobres, incluso con buena información. Actualizar tus modelos es más valioso que acumular más datos.')
                    || (unit.slug.includes('incertidumbre') && 'Porque la incertidumbre real (donde no conoces las probabilidades) requiere estrategias distintas al riesgo (donde las probabilidades son conocidas). Confundir ambos lleva a sobreconfianza peligrosa o parálisis innecesaria.')
                  )
                }
              </p>
            </div>

            {/* Enlazado interno condicional: Link a Recursos solo en programa de startups */}
            {program.slug === 'gestion-startups-agil-45' && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                    <FlaskConical className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-slate-900 mb-2">
                      Herramienta complementaria
                    </h3>
                    <p className="text-sm sm:text-base text-slate-700 mb-3 leading-relaxed">
                      Si estás aplicando estos marcos de decisión a una startup o idea de negocio, valida tu hipótesis con nuestro <strong>Simulador Lean Startup</strong>. Herramienta interactiva gratuita con metodología BUILD-MEASURE-LEARN de Eric Ries.
                    </p>
                    <Link
                      to="/recursos"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors text-sm"
                    >
                      Ir al simulador
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* Aplicación práctica */}
            <div>
              <h3 className="text-lg font-bold text-slate-900 mb-3">
                Aplicación práctica (después de escuchar)
              </h3>
              <div className="border-l-4 border-cyan-400 pl-5 space-y-2">
                <p className="text-base text-slate-700 leading-relaxed">
                  {unit.apply48h || 'Identifica una situación real en tu trabajo donde puedas aplicar estas ideas en las próximas 48 horas.'}
                </p>
                <p className="text-sm text-slate-500">
                  <strong className="text-slate-600">Consejo:</strong> No intentes aplicar todo a la vez. Elige UNA idea que resuene con tu contexto actual y úsala en una reunión o negociación próxima. La maestría viene de aplicación repetida, no de consumo pasivo.
                </p>
              </div>
            </div>

            {/* Relación con el programa padre */}
            <div>
              <h3 className="text-lg font-bold text-slate-900 mb-3">
                Relación con {program.title}
              </h3>
              <p className="text-base text-slate-700 leading-relaxed">
                Esta es la <strong>conversación {unit.order} de {program.units.length}</strong> del programa. 
                {unit.order === 1 && ' Sienta las bases  conceptuales que las siguientes conversaciones aplicarán en contextos más específicos.'}
                {unit.order === 2 && ' Profundiza en técnicas de ejecución que complementan los fundamentos establecidos en la primera conversación.'}
                {unit.order === 3 && ' Integra las ideas de las conversaciones anteriores en un framework completo de actuación profesional.'}
              </p>
              <p className="text-sm text-slate-600 mt-3">
                <Link to={`/p/${program.slug}`} className="text-cyan-600 hover:text-cyan-700 font-medium">
                  ← Ver estructura completa del programa
                </Link>
              </p>
            </div>

          </div>
        </section>

        {/* ===== CONTENIDO POST-AUDIO (Ideas centrales después de completar) ===== */}
        <section className="max-w-7xl mx-auto" ref={unlockedContentRef}>
          
          <div className="px-6 sm:px-8 pt-6 pb-16 sm:py-12 space-y-12">

          {/* Contenido desbloqueado después del audio */}
          {audioCompleted && (
            <div className="space-y-12 animate-fadeIn">
              {/* Ideas centrales de la conversación */}
              <div className="prose prose-slate max-w-none">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">
                  Ideas centrales de la conversación
                </h2>
                <ul className="space-y-3 text-slate-700 leading-relaxed">
                  {unit.bullets.map((bullet, index) => (
                    <li key={index} className="pl-0">
                      — {bullet}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Para aplicar lo escuchado */}
              <div className="bg-white rounded-lg shadow-sm p-6 sm:p-8">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">
                  Para aplicar lo escuchado
                </h2>
                <p className="text-slate-700 leading-relaxed mb-6">
                  {unit.apply48h}
                </p>
                
                {/* Línea contextual antes del CTA */}
                <p className="text-slate-500 text-sm leading-relaxed mb-3">
                  Este es el punto donde el aprendizaje se vuelve propio.
                </p>
                
                {/* CTA Reflexión guiada */}
                {isTestUnlocked ? (
                  <button
                    onClick={() => navigate(`/p/${program.slug}/u/${unit.slug}/test`)}
                    className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold transition-colors"
                  >
                    {testCompleted ? 'Ver mi perfil de resultados' : 'Realizar reflexión guiada'} 
                    <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <div className="flex items-center gap-2 text-slate-400 text-sm">
                    <Lock className="w-4 h-4" />
                    <span>Escucha la conversación completa para desbloquear la reflexión guiada</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Test de validación - Sección eliminada, integrada arriba */}

          {/* Nota al pie: Feedback (jerarquía reducida) */}
          {showFeedback && (
            <div className="border-t border-slate-200 pt-8">
              {!feedbackSubmitted ? (
                <div className="max-w-2xl">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">
                    Ayúdanos a mejorar este contenido
                  </h3>

                  {/* Rating compacto */}
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-sm text-slate-600">Calificación:</span>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => setFeedbackRating(star)}
                          className="transition-all hover:scale-110"
                        >
                          <Star
                            className={`w-5 h-5 ${
                              star <= feedbackRating
                                ? 'fill-amber-400 text-amber-400'
                                : 'text-slate-300 hover:text-amber-300'
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Selector de temas */}
                  <div className="mb-4">
                    <label className="block text-xs text-slate-600 mb-2">
                      ¿Qué temas te interesan? (opcional)
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {availableTopics.map((topic) => (
                        <button
                          key={topic}
                          onClick={() => handleTopicToggle(topic)}
                          className={`px-2.5 py-1 rounded text-xs font-medium transition-all ${
                            feedbackTopics.includes(topic)
                              ? 'bg-emerald-600 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {topic}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Campo "Otro tema" */}
                  <div className="mb-4">
                    <label className="block text-xs text-slate-600 mb-1.5">
                      ¿Otro tema de interés? (máx 2 palabras)
                    </label>
                    <input
                      type="text"
                      value={feedbackComment}
                      onChange={(e) => {
                        const value = e.target.value;
                        const words = value.trim().split(/\s+/);
                        if (words.length <= 2 && value.length <= 21) {
                          setFeedbackComment(value);
                        }
                      }}
                      placeholder="Ej: Finanzas corporativas"
                      className="w-full max-w-xs px-3 py-1.5 rounded border border-slate-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 focus:outline-none text-sm"
                      maxLength={21}
                    />
                  </div>

                  {/* Botón enviar */}
                  <button
                    onClick={handleFeedbackSubmit}
                    disabled={feedbackRating === 0}
                    className={`px-6 py-2 rounded font-medium text-sm transition-all ${
                      feedbackRating > 0
                        ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    }`}
                  >
                    Enviar
                  </button>
                </div>
              ) : (
                <div className="text-sm text-slate-600">
                  Gracias por tu feedback.
                </div>
              )}
            </div>
          )}

          {/* Navegación simplificada - solo siguiente conversación si aplica */}
          {nextUnit && isTestUnlocked && (
            <div className="flex justify-end items-center pt-8 border-t border-slate-200">
              {isNextUnitLocked ? (
                <div className="text-slate-400 text-sm font-medium flex items-center gap-2 cursor-not-allowed">
                  Siguiente conversación
                  <Lock className="w-3.5 h-3.5" />
                </div>
              ) : (
                <Link
                  to={`/p/${program.slug}/u/${nextUnit.slug}`}
                  className="text-slate-600 hover:text-slate-900 text-sm font-medium flex items-center gap-2 transition-colors"
                >
                  Siguiente conversación
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              )}
            </div>
          )}
          </div>
        </section>
      </main>
    </>
  );
}
